package com.siriusxm.unifiedcontent.console.subscriber

import androidx.compose.runtime.*
import com.siriusxm.cmc.Notification
import com.siriusxm.cmc.NotificationTypes
import com.siriusxm.pia.Application
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.DialogField
import com.siriusxm.pia.components.DialogStyles
import com.siriusxm.pia.components.NotificationBox
import com.siriusxm.pia.components.box
import com.siriusxm.pia.components.button
import com.siriusxm.pia.components.dialogOverlay
import io.ktor.client.HttpClient
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.request.*
import io.ktor.client.statement.bodyAsText
import io.ktor.http.*
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import kotlinx.serialization.json.*
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * First-play connection state. Passwords live only in the dialog's transient input state and the
 * one HTTPS request. The existing entity playback call is retried after a successful connection.
 */
object SubscriberConnection {
    var requestedEntity by mutableStateOf<String?>(null)
        private set

    private val client = HttpClient { install(HttpTimeout) { requestTimeoutMillis = 15000 } }

    fun request(entityId: String) {
        requestedEntity = entityId
    }

    fun dismiss() {
        requestedEntity = null
    }

    private suspend fun accountRequest(
        method: HttpMethod,
        body: String? = null,
        path: String = "account",
    ) {
        val token = Application.cognito.accessToken ?: error("Sign in to CMC again")
        val response =
            client.request("${window.location.origin}/sxm-playback/$path") {
                this.method = method
                header(HttpHeaders.Authorization, "Bearer $token")
                if (body != null) {
                    contentType(ContentType.Application.Json)
                    setBody(body)
                }
            }
        if (!response.status.isSuccess()) {
            val code =
                runCatching {
                        Json.parseToJsonElement(response.bodyAsText())
                            .jsonObject["code"]
                            ?.jsonPrimitive
                            ?.content
                    }
                    .getOrNull()
            error(
                when (code) {
                    "SXM_CONNECTION_REQUIRED" ->
                        "SiriusXM could not sign you in. Check your username and password."
                    "SXM_NOT_ENTITLED" ->
                        "This account does not have the required streaming subscription."
                    "RATE_LIMITED" -> "Too many attempts. Wait a minute before trying again."
                    else -> "Unable to connect to SiriusXM. Please try again."
                }
            )
        }
    }

    suspend fun connect(username: String, password: String) {
        accountRequest(
            HttpMethod.Post,
            buildJsonObject {
                    put("username", username)
                    put("password", password)
                }
                .toString(),
        )
    }

    suspend fun disconnect() {
        accountRequest(HttpMethod.Delete)
        Application.audioPlayer.controller.stop()
    }

    /**
     * Logout revokes browser grants while retaining the encrypted upstream session for next login.
     */
    fun logout() {
        Application.audioPlayer.controller.stop()
        dismiss()
        Application.scope.launch {
            try {
                if (Application.configuration.sxmPlaybackEnabled)
                    withTimeoutOrNull(2000) { accountRequest(HttpMethod.Delete, path = "grants") }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Exception) {
                // Expiry bounds the lifetime if offline; do not prevent employee logout.
            } finally {
                Application.cognito.logout()
            }
        }
    }
}

/**
 * Modal first-play/reconnect form using the shared dialog fields, error box, and footer action.
 * It submits credentials directly to the isolated subscriber
 * service, clears the password after every attempt, and resumes the original channel only after
 * success. Cancel and repeated-play requests do not retain an old password or trigger automatic
 * login retries.
 */
@Composable
fun SubscriberConnectionDialog() {
    val entity = SubscriberConnection.requestedEntity ?: return
    var username by remember(entity) { mutableStateOf("") }
    var password by remember(entity) { mutableStateOf("") }
    var busy by remember(entity) { mutableStateOf(false) }
    var errorMessage by remember(entity) { mutableStateOf<String?>(null) }
    dialogOverlay {
        title = "Connect SiriusXM"
        closeActionTitle = "Cancel"
        onClose {
            password = ""
            SubscriberConnection.dismiss()
        }
        content {
            Div({
                classes(DialogStyles.dialog)
                attr("data-dd-privacy", "hidden")
                style {
                    color(SXMUI.textColor.value())
                    padding(20.px)
                }
            }) {
                P({ style { marginTop(0.px) } }) {
                    Text("Connect your SiriusXM streaming account to play channels. You'll only be able to play channels included in your subscription.")
                }
                DialogField("SiriusXM username") {
                    Input(InputType.Text) {
                        id("sxm-username")
                        attr("aria-label", "SiriusXM username")
                        value(username)
                        attr("autocomplete", "username")
                        if (busy) attr("disabled", "")
                        onInput { username = it.value }
                    }
                }
                DialogField("Password", instruction = "Your password is not saved.") {
                    Input(InputType.Password) {
                        id("sxm-password")
                        attr("aria-label", "Password")
                        value(password)
                        attr("autocomplete", "current-password")
                        if (busy) attr("disabled", "")
                        onInput { password = it.value }
                    }
                }
                errorMessage?.let { message ->
                    Div({ attr("role", "alert") }) {
                        NotificationBox(Notification(NotificationTypes.Error, message))
                    }
                }
            }
        }
        action {
            title = if (busy) "Connecting…" else "Connect and play"
            primary = true
            showProgressOnAction = true
            enabled = !busy && username.isNotBlank() && password.isNotEmpty()
            action {
                busy = true
                errorMessage = null
                val submittedPassword = password
                password = ""
                try {
                    SubscriberConnection.connect(username, submittedPassword)
                    if (SubscriberConnection.requestedEntity == entity) {
                        SubscriberConnection.dismiss()
                        Application.playEntity(entity)
                    }
                } catch (cancellation: CancellationException) {
                    throw cancellation
                } catch (error: Exception) {
                    errorMessage = error.message
                } finally {
                    busy = false
                }
            }
        }
    }
}

/**
 * Account-page SiriusXM box explaining the encrypted session used for channel playback.
 * Offers an explicit disconnect action without displaying saved session data or credentials.
 */
@Composable
fun SubscriberAccountControls() {
    if (!Application.configuration.sxmPlaybackEnabled) return
    box("SiriusXM") {
        P {
            Text(
                "When you connect your SiriusXM account, CMC stores an encrypted session for up to " +
                    "90 days so you can play channels included in your subscription. " +
                    "Your username and password are not stored. Disconnect to remove the saved " +
                    "session; you'll be asked to sign in again the next time you play a channel."
            )
        }
        button {
            title = "Disconnect SiriusXM"
            showProgressOnAction = true
            action {
                try {
                    SubscriberConnection.disconnect()
                    Application.context.info("SiriusXM disconnected")
                } catch (cancellation: CancellationException) {
                    throw cancellation
                } catch (_: Exception) {
                    Application.context.showError("Unable to disconnect SiriusXM")
                }
            }
        }
    }
}
